import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, concat, finalize, map, Observable, of, shareReplay } from 'rxjs';
import { parseQueryObject } from 'src/app/classes/shared/service.tools';
import { Model, PutModel } from 'src/app/interfaces/api/model';
import { Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';
import { PlanningStore } from 'src/app/services/stores/planning.store';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../core/api.service';
import { AuthService } from '../../core/auth.service';
import { RequestService } from '../../core/request.service';
import { WebSocketService } from '../../core/web-socket.service';
import { ProjectService } from '../projects/project.service';

type QueryParams = Record<string, string | number | boolean | Date | Array<string | number | boolean | Date> | object>;

@Injectable({
  providedIn: 'root'
})
export class ModelService {
  // Services
  public readonly http: HttpClient = inject(HttpClient);
  private readonly authService: AuthService = inject(AuthService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly request: RequestService = inject(RequestService);
  private readonly socket: WebSocketService = inject(WebSocketService);
  private readonly apiService: ApiService = inject(ApiService);
  private readonly planningStore: PlanningStore = inject(PlanningStore);

  // Variables
  collection = "Model";
  collectionPath = "models";
  models: Model[] = [];
  apiUrl = environment.API_URL;
  private currentModelSubject: BehaviorSubject<Model>;
  public currentModel: Observable<Model>;
  user: User;
  project: Project | null;

  constructor() {
    this.user = this.authService.currentUser();
    this.project = this.projectService.currentProjectValue;

    const storedModel = localStorage.getItem('model');
    this.currentModelSubject = new BehaviorSubject<any>(storedModel ? JSON.parse(storedModel) : null);
    this.currentModel = this.currentModelSubject.asObservable();
  }

  public get currentModelValue(): any {
    return this.currentModelSubject.value;
  }

  getModel(id: string): Observable<Model> {
    this.planningStore.isLoading('model', true);

    return this.request.getItem(this.collection, this.collectionPath, id).pipe(
      finalize(() => this.planningStore.isLoading('model', false))
    );
  }

  getModels(projects: any = []): Observable<Model[]> {
    return concat(
      this.request.getCollection(this.collection, this.collectionPath, { projects }),
      this.socket.getUpdate(this.collection)
    )
  }

  private formats$?: Observable<string[] | null>;

  getFormats(): Observable<string[] | null> {
    this.formats$ ??= this.apiService.getCollection(this.collectionPath, 'formats', {}).pipe(
      map((res: any) => Array.isArray(res?.extensions) ? res.extensions.map((ext: string) => ext.toLowerCase()) : null),
      catchError(() => {
        this.formats$ = undefined;
        return of(null);
      }),
      shareReplay(1)
    );
    return this.formats$;
  }

  getToken(): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'token', {})
  }

  getManifests(query: QueryParams = {}): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'manifest', query);
  }

  getThumbnail(modelUrn: string): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'thumbnail', { urn: modelUrn });
  }

  getCrane(id: string): Observable<any> {
    return this.apiService.getCollectionItem(this.collectionPath, 'crane', id);
  }

  getCurrentModel(project: string): Observable<Model> {
    return this.apiService.getCollection(this.collectionPath, 'current', project);
  }

  //---POST---//

  addModel(model: any): Observable<any> {
    let data = new FormData();

    if (model['file'])
      data.append('file', model['file']);

    Object.keys(model).forEach(key => {
      if (key !== 'file')
        data.append(key, JSON.stringify(model[key]));
    });
    return this.request.postItem(this.collection, this.collectionPath, data);
  }


  uploadModel(model: any, options: any): Observable<any> {
    let data = new FormData();
    data.append('model', model, model.name)
    data.append('type', options.type)
    // return this.http.post(`${this.apiUrl}/api/models/upload`, data, { reportProgress: true, observe: 'events' });
    return this.request.postItem(this.collection, this.collectionPath + '/upload', data, { reportProgress: true, observe: 'events' });
  }

  uploadImage(name: string, image: File, model: string) {
    let data = new FormData();
    data.append(name, image, image.name);
    data.append('model', model);

    return this.request.postItem(this.collection, this.collectionPath + '/upload-image', data);
  }

  addCrane(cranes: QueryParams, modelId: string) {//cranes must be an array
    return this.apiService.postCollectionData(this.collectionPath, 'crane/' + modelId, cranes);
  }

  removeCrane(cranes: QueryParams, modelId: string): Observable<any> {
    // return this.http.post(this.API_URL + '/api/models/crane/' + modelId, { cranes })
    return this.apiService.postCollectionData(this.collectionPath, 'crane/' + modelId, cranes);
  }

  //---PUT---//
  updateModel(model: PutModel): Observable<any> {
    return this.request.setProjectData(this.collection, this.collectionPath, model);
  }

  //---DELETE---//

  removeModel(id: string): Observable<any> {
    return this.request.deleteItem(this.collection, this.collectionPath, id);
  }

  removeModeles(query: QueryParams = {}): Observable<any> {
    let parseQuery: string = ""
    if (query)
      parseQuery = parseQueryObject(query);
    return this.http.delete(this.apiUrl + '/api/models' + parseQuery)
  }

}
